import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Optional, Inject, Directive, APP_INITIALIZER, NgModule } from '@angular/core';
import * as i4 from '@angular/common';
import { DOCUMENT, CommonModule } from '@angular/common';
import * as i1 from '@angular/common/http';
import { HttpParams, HttpHeaders, HTTP_INTERCEPTORS } from '@angular/common/http';
import { Subject, from, of } from 'rxjs';
import { filter, tap, debounceTime, map, switchMap, delay } from 'rxjs/operators';
import * as i2 from '@angular/router';
import * as rs from 'jsrsasign';

class AuthEvent {
    type;
    constructor(type) {
        this.type = type;
    }
}
class AuthSuccessEvent extends AuthEvent {
    info;
    constructor(type, info = null) {
        super(type);
        this.info = info;
    }
}
class AuthInfoEvent extends AuthEvent {
    info;
    constructor(type, info = null) {
        super(type);
        this.info = info;
    }
}
class AuthErrorEvent extends AuthEvent {
    reason;
    params;
    constructor(type, reason, params = null) {
        super(type);
        this.reason = reason;
        this.params = params;
    }
}

// see: https://developer.mozilla.org/en-US/docs/Web/API/WindowBase64/Base64_encoding_and_decoding#The_.22Unicode_Problem.22
function b64DecodeUnicode(str) {
    const base64 = str.replace(/\-/g, '+').replace(/\_/g, '/');
    return decodeURIComponent(atob(base64)
        .split('')
        .map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    })
        .join(''));
}
function padBase64(base64data) {
    while (base64data.length % 4 !== 0) {
        base64data += '=';
    }
    return base64data;
}
function base64UrlEncode(str) {
    const base64 = btoa(str);
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

const AUTH_CONFIG = new InjectionToken('AUTH_CONFIG');
class AuthConfig {
    /**
     * Is the authentication required on application launch
     */
    authOnAppInitializer;
    /**
     * In the case of you have configured on redirect uri in the IAM
     * You can force the redirect on the targeted uri in an app initializer function
     */
    redirectOnAppInitializer;
    /**
     * User info endpoint could be call automatically after the login process
     */
    triggerUserInfoAfterLogin;
    /**
     * The client's id as registered with the auth server
     */
    clientId = '';
    /**
     * The client's redirectUri as registered with the auth server
     */
    redirectUri = '';
    /**
     * Allow URL
     * Use to send access token
     */
    bearerAllowedUrls;
    /**
     * An optional second redirectUri where the auth server
     * redirects the user to after logging out.
     */
    postLogoutRedirectUri = '';
    /**
     * The auth server's endpoint that allows to log
     * the user in when using implicit flow.
     */
    loginUrl = '';
    /**
     * The requested scopes
     */
    scope = 'openid profile';
    resource = '';
    /**
     * The issuer's uri.
     */
    issuer = '';
    /**
     * The logout url.
     */
    logoutUrl = '';
    /**
     * Url of the token endpoint as defined by OpenId Connect and OAuth 2.
     */
    tokenEndpoint = null;
    /**
     * Url of the revocation endpoint as defined by OpenId Connect and OAuth 2.
     */
    revocationEndpoint = null;
    /**
     * Url of the userinfo endpoint as defined by OpenId Connect.
     */
    userinfoEndpoint = null;
    /**
     * Send scope in refresh token request
     */
    refreshTokenScope = true;
    /**
     * Some auth servers don't allow using password flow
     * w/o a client secret while the standards do not
     * demand for it. In this case, you can set a password
     * here. As this password is exposed to the public
     * it does not bring additional security and is therefore
     * as good as using no password.
     */
    clientSecret = null;
    /**
     * JSON Web Key Set (https://tools.ietf.org/html/rfc7517)
     * with keys used to validate received id_tokens.
     * This is taken out of the disovery document. Can be set manually too.
     */
    jwks = null;
    /**
     * Logout url is call in another browser tab
     * Mandatory for the moment with Salesforce ID
     */
    logoutOnBlankTab = false;
    /**
     * Defines when the token_timeout event should be raised.
     * If you set this to the default value 0.75, the event
     * is triggered after 75% of the token's life time.
     */
    timeoutFactor = 0.75;
    /**
     * If true, the lib will try to check whether the user
     * is still logged in on a regular basis as described
     * in http://openid.net/specs/openid-connect-session-1_0.html#ChangeNotification
     */
    sessionChecksEnabled = false;
    /**
     * Interval in msec for checking the session
     * according to http://openid.net/specs/openid-connect-session-1_0.html#ChangeNotification
     */
    sessionCheckIntervall = 3 * 1000;
    /**
     * Url for the iframe used for session checks
     */
    sessionCheckIFrameUrl = null;
    /**
     * Name of the iframe to use for session checks
     */
    sessionCheckIFrameName = 'mic-auth-oidc-check-session-iframe';
    /**
     * Name of the iframe to use for session checks
     */
    logoutIFrameName = 'mic-auth-oidc-logout-iframe';
    /**
     * Defines wether to check the subject of a refreshed token after silent refresh.
     * Normally, it should be the same as before.
     */
    skipSubjectCheck = false;
    /**
     * Defined whether to skip the validation of the issuer in the discovery document.
     * Normally, the discovey document's url starts with the url of the issuer.
     */
    skipIssuerCheck = false;
    /**
     * According to rfc6749 it is recommended (but not required) that the auth
     * server exposes the access_token's life time in seconds.
     * This is a fallback value for the case this value is not exposed.
     */
    fallbackAccessTokenExpirationTimeInSec;
    /**
     * final state sent to issuer is built as follows:
     * state = nonce + nonceStateSeparator + additional state
     * Default separator is ';' (encoded %3B).
     * In rare cases, this character might be forbidden or inconvenient to use by the issuer so it can be customized.
     */
    nonceStateSeparator = ';';
    /**
     * Code Flow is by default used together with PKCI which is also higly recommented.
     * You can disbale it here by setting this flag to true.
     * https://tools.ietf.org/html/rfc7636#section-1.1
     */
    disablePKCE = false;
    /**
     * Nonce check is recommented on implicit flow
     * For authorization code flow, by default, the validation is disabled.
     */
    disableNonceCheck = true;
    /**
     * Fallback route when login process is in error
     */
    errorFallbackRoute = '/error';
    constructor(json) {
        if (json) {
            Object.assign(this, json);
        }
    }
    /**
     * This property allows you to override the method that is used to open the login url,
     * allowing a way for implementations to specify their own method of routing to new
     * urls.
     */
    openUri = (uri) => {
        location.href = uri;
    };
}

/**
 * This custom encoder allows charactes like +, % and / to be used in passwords
 */
class WebHttpUrlEncodingCodec {
    encodeKey(k) {
        return encodeURIComponent(k);
    }
    encodeValue(v) {
        return encodeURIComponent(v);
    }
    decodeKey(k) {
        return decodeURIComponent(k);
    }
    decodeValue(v) {
        return decodeURIComponent(v);
    }
}
function decodeUTF8(s) {
    if (typeof s !== 'string') {
        throw new TypeError('expected string');
    }
    let i;
    const d = s;
    let b = new Uint8Array(d.length);
    for (i = 0; i < d.length; i++) {
        b[i] = d.charCodeAt(i);
    }
    return b;
}
function encodeUTF8(arr) {
    let i;
    const s = [];
    for (i = 0; i < arr.length; i++) {
        s.push(String.fromCharCode(arr[i]));
    }
    return s.join('');
}

/* eslint-disable one-var */
class UrlService {
    getHashFragmentParams(customHashFragment) {
        let hash = customHashFragment || window.location.hash;
        hash = decodeURIComponent(hash);
        if (hash.indexOf('#') !== 0) {
            return {};
        }
        const questionMarkPosition = hash.indexOf('?');
        if (questionMarkPosition > -1) {
            hash = hash.substring(questionMarkPosition + 1);
        }
        else {
            hash = hash.substring(1);
        }
        return this.parseQueryString(hash);
    }
    parseQueryString(queryString) {
        const data = {};
        let pairs, pair, separatorIndex, escapedKey, escapedValue, key, value;
        if (queryString === null) {
            return data;
        }
        pairs = queryString.split('&');
        for (let i = 0; i < pairs.length; i++) {
            pair = pairs[i];
            separatorIndex = pair.indexOf('=');
            if (separatorIndex === -1) {
                escapedKey = pair;
                escapedValue = null;
            }
            else {
                escapedKey = pair.substring(0, separatorIndex);
                escapedValue = pair.substring(separatorIndex + 1);
            }
            key = decodeURIComponent(escapedKey);
            value = decodeURIComponent(escapedValue);
            if (key.substring(0, 1) === '/') {
                key = key.substring(1);
            }
            data[key] = value;
        }
        return data;
    }
    /**
     * Retrieve the returned auth code from the redirect uri that has been called.
     * If required also check hash, as we could use hash location strategy.
     */
    getCodePartsFromUrl(queryString) {
        if (!queryString || queryString.length === 0) {
            return this.getHashFragmentParams();
        }
        // normalize query string
        if (queryString.charAt(0) === '?') {
            queryString = queryString.substring(1);
        }
        return this.parseQueryString(queryString);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: UrlService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: UrlService });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: UrlService, decorators: [{
            type: Injectable
        }] });

/**
 * Defines a simple storage that can be used for
 * storing the tokens at client side.
 * Is compatible to localStorage and sessionStorage,
 * but you can also create your own implementations.
 */
class AuthStorage {
}
/**
 * Additional options that can be passed to tryLogin.
 */
class LoginOptions {
    /**
     * A custom hash fragment to be used instead of the
     * actual one. This is used for silent refreshes, to
     * pass the iframes hash fragment to this method, and
     * is also used by popup flows in the same manner.
     * This can be used with code flow, where is must be set
     * to a hash symbol followed by the querystring. The
     * question mark is optional, but may be present following
     * the hash symbol.
     */
    customHashFragment;
    /**
     * Set this for code flow if you used a custom redirect Uri
     * when retrieving the code. This is used internally for silent
     * refresh and popup flows.
     */
    customRedirectUri;
}
/**
 * Represents the received tokens, the received state
 * and the parsed claims from the id-token.
 */
class ReceivedTokens {
    idToken;
    accessToken;
    idClaims;
    state;
}

/**
 * Interface for Handlers that are hooked in to
 * validate tokens.
 */
class ValidationHandler {
}
/**
 * This abstract implementation of ValidationHandler already implements
 * the method validateAtHash. However, to make use of it,
 * you have to override the method calcHash.
 */
class AbstractValidationHandler {
    /**
     * Validates the at_hash in an id_token against the received access_token.
     */
    async validateAtHash(params) {
        const hashAlg = this.inferHashAlgorithm(params.idTokenHeader);
        const tokenHash = await this.calcHash(params.accessToken, hashAlg);
        const leftMostHalf = tokenHash.substring(0, tokenHash.length / 2);
        const atHash = base64UrlEncode(leftMostHalf);
        const claimsAtHash = params.idTokenClaims.at_hash.replace(/=/g, '');
        if (atHash !== claimsAtHash) {
            console.error('exptected at_hash: ' + atHash);
            console.error('actual at_hash: ' + claimsAtHash);
        }
        return atHash === claimsAtHash;
    }
    /**
     * Infers the name of the hash algorithm to use
     * from the alg field of an id_token.
     *
     * @param jwtHeader the id_token's parsed header
     */
    inferHashAlgorithm(jwtHeader) {
        let alg = jwtHeader['alg'];
        if (!alg.match(/^.S[0-9]{3}$/)) {
            throw new Error('Algorithm not supported: ' + alg);
        }
        return 'sha-' + alg.substring(2);
    }
}

// SHA-256 (+ HMAC and PBKDF2) for JavaScript.
//
// Written in 2014-2016 by Dmitry Chestnykh.
// Public domain, no warranty.
//
// Functions (accept and return Uint8Arrays):
//
//   sha256(message) -> hash
//   sha256.hmac(key, message) -> mac
//   sha256.pbkdf2(password, salt, rounds, dkLen) -> dk
//
//  Classes:
//
//   new sha256.Hash()
//   new sha256.HMAC(key)
//
const digestLength = 32;
const blockSize = 64;
// SHA-256 constants
const K = new Uint32Array([
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5, 0xd807aa98, 0x12835b01, 0x243185be,
    0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174, 0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa,
    0x5cb0a9dc, 0x76f988da, 0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967, 0x27b70a85,
    0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85, 0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3,
    0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070, 0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f,
    0x682e6ff3, 0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2
]);
function hashBlocks(w, v, p, pos, len) {
    let a;
    let b;
    let c;
    let d;
    let e;
    let f;
    let g;
    let h;
    let u;
    let i;
    let j;
    let t1;
    let t2;
    while (len >= 64) {
        a = v[0];
        b = v[1];
        c = v[2];
        d = v[3];
        e = v[4];
        f = v[5];
        g = v[6];
        h = v[7];
        for (i = 0; i < 16; i++) {
            j = pos + i * 4;
            w[i] = ((p[j] & 0xff) << 24) | ((p[j + 1] & 0xff) << 16) | ((p[j + 2] & 0xff) << 8) | (p[j + 3] & 0xff);
        }
        for (i = 16; i < 64; i++) {
            u = w[i - 2];
            t1 = ((u >>> 17) | (u << (32 - 17))) ^ ((u >>> 19) | (u << (32 - 19))) ^ (u >>> 10);
            u = w[i - 15];
            t2 = ((u >>> 7) | (u << (32 - 7))) ^ ((u >>> 18) | (u << (32 - 18))) ^ (u >>> 3);
            w[i] = ((t1 + w[i - 7]) | 0) + ((t2 + w[i - 16]) | 0);
        }
        for (i = 0; i < 64; i++) {
            t1 =
                ((((((e >>> 6) | (e << (32 - 6))) ^ ((e >>> 11) | (e << (32 - 11))) ^ ((e >>> 25) | (e << (32 - 25)))) + ((e & f) ^ (~e & g))) |
                    0) +
                    ((h + ((K[i] + w[i]) | 0)) | 0)) |
                    0;
            t2 =
                ((((a >>> 2) | (a << (32 - 2))) ^ ((a >>> 13) | (a << (32 - 13))) ^ ((a >>> 22) | (a << (32 - 22)))) +
                    ((a & b) ^ (a & c) ^ (b & c))) |
                    0;
            h = g;
            g = f;
            f = e;
            e = (d + t1) | 0;
            d = c;
            c = b;
            b = a;
            a = (t1 + t2) | 0;
        }
        v[0] += a;
        v[1] += b;
        v[2] += c;
        v[3] += d;
        v[4] += e;
        v[5] += f;
        v[6] += g;
        v[7] += h;
        pos += 64;
        len -= 64;
    }
    return pos;
}
// Hash implements SHA256 hash algorithm.
class Hash {
    digestLength = digestLength;
    blockSize = blockSize;
    // Note: Int32Array is used instead of Uint32Array for performance reasons.
    state = new Int32Array(8); // hash state
    temp = new Int32Array(64); // temporary state
    buffer = new Uint8Array(128); // buffer for data to hash
    bufferLength = 0; // number of bytes in buffer
    bytesHashed = 0; // number of total bytes hashed
    finished = false; // indicates whether the hash was finalized
    constructor() {
        this.reset();
    }
    // Resets hash state making it possible
    // to re-use this instance to hash other data.
    reset() {
        this.state[0] = 0x6a09e667;
        this.state[1] = 0xbb67ae85;
        this.state[2] = 0x3c6ef372;
        this.state[3] = 0xa54ff53a;
        this.state[4] = 0x510e527f;
        this.state[5] = 0x9b05688c;
        this.state[6] = 0x1f83d9ab;
        this.state[7] = 0x5be0cd19;
        this.bufferLength = 0;
        this.bytesHashed = 0;
        this.finished = false;
        return this;
    }
    // Cleans internal buffers and re-initializes hash state.
    clean() {
        for (let i = 0; i < this.buffer.length; i++) {
            this.buffer[i] = 0;
        }
        for (let i = 0; i < this.temp.length; i++) {
            this.temp[i] = 0;
        }
        this.reset();
    }
    // Updates hash state with the given data.
    //
    // Optionally, length of the data can be specified to hash
    // fewer bytes than data.length.
    //
    // Throws error when trying to update already finalized hash:
    // instance must be reset to use it again.
    update(data, dataLength = data.length) {
        if (this.finished) {
            throw new Error("SHA256: can't update because hash was finished.");
        }
        let dataPos = 0;
        this.bytesHashed += dataLength;
        if (this.bufferLength > 0) {
            while (this.bufferLength < 64 && dataLength > 0) {
                this.buffer[this.bufferLength++] = data[dataPos++];
                dataLength--;
            }
            if (this.bufferLength === 64) {
                hashBlocks(this.temp, this.state, this.buffer, 0, 64);
                this.bufferLength = 0;
            }
        }
        if (dataLength >= 64) {
            dataPos = hashBlocks(this.temp, this.state, data, dataPos, dataLength);
            dataLength %= 64;
        }
        while (dataLength > 0) {
            this.buffer[this.bufferLength++] = data[dataPos++];
            dataLength--;
        }
        return this;
    }
    // Finalizes hash state and puts hash into out.
    //
    // If hash was already finalized, puts the same value.
    finish(out) {
        if (!this.finished) {
            const bytesHashed = this.bytesHashed;
            const left = this.bufferLength;
            const bitLenHi = (bytesHashed / 0x20000000) | 0;
            const bitLenLo = bytesHashed << 3;
            const padLength = bytesHashed % 64 < 56 ? 64 : 128;
            this.buffer[left] = 0x80;
            for (let i = left + 1; i < padLength - 8; i++) {
                this.buffer[i] = 0;
            }
            this.buffer[padLength - 8] = (bitLenHi >>> 24) & 0xff;
            this.buffer[padLength - 7] = (bitLenHi >>> 16) & 0xff;
            this.buffer[padLength - 6] = (bitLenHi >>> 8) & 0xff;
            this.buffer[padLength - 5] = (bitLenHi >>> 0) & 0xff;
            this.buffer[padLength - 4] = (bitLenLo >>> 24) & 0xff;
            this.buffer[padLength - 3] = (bitLenLo >>> 16) & 0xff;
            this.buffer[padLength - 2] = (bitLenLo >>> 8) & 0xff;
            this.buffer[padLength - 1] = (bitLenLo >>> 0) & 0xff;
            hashBlocks(this.temp, this.state, this.buffer, 0, padLength);
            this.finished = true;
        }
        for (let i = 0; i < 8; i++) {
            out[i * 4 + 0] = (this.state[i] >>> 24) & 0xff;
            out[i * 4 + 1] = (this.state[i] >>> 16) & 0xff;
            out[i * 4 + 2] = (this.state[i] >>> 8) & 0xff;
            out[i * 4 + 3] = (this.state[i] >>> 0) & 0xff;
        }
        return this;
    }
    // Returns the final hash digest.
    digest() {
        const out = new Uint8Array(this.digestLength);
        this.finish(out);
        return out;
    }
    // Internal function for use in HMAC for optimization.
    _saveState(out) {
        for (let i = 0; i < this.state.length; i++) {
            out[i] = this.state[i];
        }
    }
    // Internal function for use in HMAC for optimization.
    _restoreState(from, bytesHashed) {
        for (let i = 0; i < this.state.length; i++) {
            this.state[i] = from[i];
        }
        this.bytesHashed = bytesHashed;
        this.finished = false;
        this.bufferLength = 0;
    }
}
// HMAC implements HMAC-SHA256 message authentication algorithm.
class HMAC {
    inner = new Hash();
    outer = new Hash();
    blockSize = this.inner.blockSize;
    digestLength = this.inner.digestLength;
    // Copies of hash states after keying.
    // Need for quick reset without hashing they key again.
    istate;
    ostate;
    constructor(key) {
        const pad = new Uint8Array(this.blockSize);
        if (key.length > this.blockSize) {
            new Hash().update(key).finish(pad).clean();
        }
        else {
            for (let i = 0; i < key.length; i++) {
                pad[i] = key[i];
            }
        }
        for (let i = 0; i < pad.length; i++) {
            pad[i] ^= 0x36;
        }
        this.inner.update(pad);
        for (let i = 0; i < pad.length; i++) {
            pad[i] ^= 0x36 ^ 0x5c;
        }
        this.outer.update(pad);
        this.istate = new Uint32Array(8);
        this.ostate = new Uint32Array(8);
        this.inner._saveState(this.istate);
        this.outer._saveState(this.ostate);
        for (let i = 0; i < pad.length; i++) {
            pad[i] = 0;
        }
    }
    // Returns HMAC state to the state initialized with key
    // to make it possible to run HMAC over the other data with the same
    // key without creating a new instance.
    reset() {
        this.inner._restoreState(this.istate, this.inner.blockSize);
        this.outer._restoreState(this.ostate, this.outer.blockSize);
        return this;
    }
    // Cleans HMAC state.
    clean() {
        for (let i = 0; i < this.istate.length; i++) {
            this.ostate[i] = this.istate[i] = 0;
        }
        this.inner.clean();
        this.outer.clean();
    }
    // Updates state with provided data.
    update(data) {
        this.inner.update(data);
        return this;
    }
    // Finalizes HMAC and puts the result in out.
    finish(out) {
        if (this.outer.finished) {
            this.outer.finish(out);
        }
        else {
            this.inner.finish(out);
            this.outer.update(out, this.digestLength).finish(out);
        }
        return this;
    }
    // Returns message authentication code.
    digest() {
        const out = new Uint8Array(this.digestLength);
        this.finish(out);
        return out;
    }
}
// Returns SHA256 hash of data.
function hash(data) {
    const h = new Hash().update(data);
    const digest = h.digest();
    h.clean();
    return digest;
}
// Returns HMAC-SHA256 of data under the key.
function hmac(key, data) {
    const h = new HMAC(key).update(data);
    const digest = h.digest();
    h.clean();
    return digest;
}
// Fills hkdf buffer like this:
// T(1) = HMAC-Hash(PRK, T(0) | info | 0x01)
function fillBuffer(buffer, pHmac, info, counter) {
    // Counter is a byte value: check if it overflowed.
    const num = counter[0];
    if (num === 0) {
        throw new Error('hkdf: cannot expand more');
    }
    // Prepare HMAC instance for new data with old key.
    pHmac.reset();
    // Hash in previous output if it was generated
    // (i.e. counter is greater than 1).
    if (num > 1) {
        pHmac.update(buffer);
    }
    // Hash in info if it exists.
    if (info) {
        pHmac.update(info);
    }
    // Hash in the counter.
    pHmac.update(counter);
    // Output result to buffer and clean HMAC instance.
    pHmac.finish(buffer);
    // Increment counter inside typed array, this works properly.
    counter[0]++;
}
const hkdfSalt = new Uint8Array(digestLength); // Filled with zeroes.
function hkdf(key, salt = hkdfSalt, info, length = 32) {
    const counter = new Uint8Array([1]);
    // HKDF-Extract uses salt as HMAC key, and key as data.
    const okm = hmac(salt, key);
    // Initialize HMAC for expanding with extracted key.
    // Ensure no collisions with `hmac` function.
    const hmac_ = new HMAC(okm);
    // Allocate buffer.
    const buffer = new Uint8Array(hmac_.digestLength);
    let bufpos = buffer.length;
    const out = new Uint8Array(length);
    for (let i = 0; i < length; i++) {
        if (bufpos === buffer.length) {
            fillBuffer(buffer, hmac_, info, counter);
            bufpos = 0;
        }
        out[i] = buffer[bufpos++];
    }
    hmac_.clean();
    buffer.fill(0);
    counter.fill(0);
    return out;
}
// Derives a key from password and salt using PBKDF2-HMAC-SHA256
// with the given number of iterations.
//
// The number of bytes returned is equal to dkLen.
//
// (For better security, avoid dkLen greater than hash length - 32 bytes).
function pbkdf2(password, salt, iterations, dkLen) {
    const prf = new HMAC(password);
    const len = prf.digestLength;
    const ctr = new Uint8Array(4);
    const t = new Uint8Array(len);
    const u = new Uint8Array(len);
    const dk = new Uint8Array(dkLen);
    for (let i = 0; i * len < dkLen; i++) {
        let c = i + 1;
        ctr[0] = (c >>> 24) & 0xff;
        ctr[1] = (c >>> 16) & 0xff;
        ctr[2] = (c >>> 8) & 0xff;
        ctr[3] = (c >>> 0) & 0xff;
        prf.reset();
        prf.update(salt);
        prf.update(ctr);
        prf.finish(u);
        for (let j = 0; j < len; j++) {
            t[j] = u[j];
        }
        for (let j = 2; j <= iterations; j++) {
            prf.reset();
            prf.update(u).finish(u);
            for (let k = 0; k < len; k++) {
                t[k] ^= u[k];
            }
        }
        for (let j = 0; j < len && i * len + j < dkLen; j++) {
            dk[i * len + j] = t[j];
        }
    }
    for (let i = 0; i < len; i++) {
        t[i] = u[i] = 0;
    }
    for (let i = 0; i < 4; i++) {
        ctr[i] = 0;
    }
    prf.clean();
    return dk;
}

/**
 * Abstraction for crypto algorithms
 */
class HashHandler {
}
class DefaultHashHandler {
    calcHash(valueToHash) {
        return encodeUTF8(hash(decodeUTF8(valueToHash)));
    }
    toHashString2(byteArray) {
        let result = '';
        for (const e of byteArray) {
            result += String.fromCharCode(e);
        }
        return result;
    }
    toHashString(buffer) {
        const byteArray = new Uint8Array(buffer);
        let result = '';
        for (const e of byteArray) {
            result += String.fromCharCode(e);
        }
        return result;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: DefaultHashHandler, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: DefaultHashHandler });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: DefaultHashHandler, decorators: [{
            type: Injectable
        }] });

/**
 * Service for logging in and logging out with
 * OIDC and OAuth2. Supports implicit flow and
 * password flow.
 */
class AuthService {
    ngZone;
    http;
    router;
    urlHelper;
    platformLocation;
    tokenValidationHandler;
    crypto;
    document;
    config;
    /**
     * Informs about events, like token_received or token_expires.
     * See the string enum EventType for a full list of event types.
     */
    events;
    /**
     * The received (passed around) state, when logging
     * in with implicit flow.
     */
    state = '';
    eventsSubject = new Subject();
    silentRefreshPostMessageEventListener;
    grantTypesSupported = [];
    storage = null;
    accessTokenTimeoutSubscription;
    idTokenTimeoutSubscription;
    tokenReceivedSubscription;
    automaticRefreshSubscription;
    sessionCheckEventListener;
    jwksUri;
    sessionCheckTimer;
    silentRefreshSubject;
    clockSkewInSec = 600;
    userActivityTimer;
    discovered = false;
    constructor(ngZone, http, router, urlHelper, platformLocation, storage, tokenValidationHandler, crypto, document, config) {
        this.ngZone = ngZone;
        this.http = http;
        this.router = router;
        this.urlHelper = urlHelper;
        this.platformLocation = platformLocation;
        this.tokenValidationHandler = tokenValidationHandler;
        this.crypto = crypto;
        this.document = document;
        this.config = config;
        this.events = this.eventsSubject.asObservable();
        this.storage = storage || sessionStorage;
        this.configure();
        this.setupRefreshTimer();
    }
    /**
     * Set the user inactivity timer
     *
     * @param milliseconds
     */
    setIdleTimeout(milliseconds) {
        if (this.userActivityTimer) {
            clearTimeout(this.userActivityTimer);
        }
        this.userActivityTimer = setTimeout(() => {
            this.logOut();
        }, milliseconds);
    }
    /**
     * Use this method to configure the service
     */
    configure() {
        if (this.config.sessionChecksEnabled) {
            this.setupSessionCheck();
        }
        this.setupRefreshTimer();
    }
    /**
     * Restart session check if configuration allow this behaviour
     */
    restartSessionChecksIfStillLoggedIn() {
        if (this.hasValidIdToken()) {
            this.initSessionCheck();
        }
    }
    setupSessionCheck() {
        this.events.pipe(filter((e) => e.type === 'token_received')).subscribe((e) => {
            this.initSessionCheck();
        });
    }
    /**
     * Will setup up silent refreshing for when the token is
     * about to expire. When the user is logged out via this.logOut method, the
     * silent refreshing will pause and not refresh the tokens until the user is
     * logged back in via receiving a new token.
     *
     * @param listenTo Setup automatic refresh of a specific token type
     */
    setupAutomaticSilentRefresh(listenTo) {
        let shouldRunSilentRefresh = true;
        this.clearAutomaticRefreshTimer();
        if (this.automaticRefreshSubscription) {
            this.automaticRefreshSubscription.unsubscribe();
        }
        this.automaticRefreshSubscription = this.events
            .pipe(tap((e) => {
            if (e.type === 'token_received') {
                shouldRunSilentRefresh = true;
            }
            else if (e.type === 'logout') {
                shouldRunSilentRefresh = false;
            }
        }), filter((e) => e.type === 'token_expires' && (listenTo == null || listenTo === 'any' || e.info === listenTo)), debounceTime(1000))
            .subscribe((_) => {
            if (shouldRunSilentRefresh) {
                return this.refreshToken();
            }
        });
        this.setupExpirationTimers();
    }
    /**
     * After login process, if a route is desired
     * Navigate on the route
     */
    navigateToRequestedRoute() {
        const requestedRoute = this.storage.getItem('requested_route');
        if (requestedRoute) {
            const baseHref = this.platformLocation.getBaseHrefFromDOM();
            const routeWithParameters = requestedRoute.slice(baseHref.length).split('?');
            const route = routeWithParameters[0];
            const queryParametersString = routeWithParameters[1];
            if (queryParametersString) {
                const queryParametersArray = queryParametersString.split('&');
                const queryParams = {};
                queryParametersArray.forEach((param) => {
                    const split = param.split('=');
                    const key = split[0];
                    const value = split[1];
                    queryParams[key] = value;
                });
                this.router.navigate([route], { queryParams });
            }
            else {
                this.router.navigate([route]);
            }
        }
    }
    /**
     * After login process, if an error occurs
     * Navigate on the route
     */
    navigateToErrorRoute() {
        this.router.navigate([this.config.errorFallbackRoute]);
    }
    /**
     * Refreshes the token using a refresh_token.
     * This does not work for implicit flow, b/c
     * there is no refresh_token in this flow.
     * A solution for this is provided by the
     * method silentRefresh.
     */
    refreshToken() {
        return new Promise((resolve, reject) => {
            let params = new HttpParams({ encoder: new WebHttpUrlEncodingCodec() })
                .set('grant_type', 'refresh_token')
                .set('refresh_token', this.storage.getItem('refresh_token'));
            if (this.config.refreshTokenScope) {
                params = params.set('scope', this.config.scope);
            }
            const headers = new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded');
            params = params.set('client_id', this.config?.clientId);
            if (this.config.clientSecret) {
                params = params.set('client_secret', this.config.clientSecret);
            }
            this.http
                .post(this.config.tokenEndpoint, params, { headers })
                .pipe(switchMap((tokenResponse) => {
                if (tokenResponse.id_token) {
                    return from(this.processIdToken(tokenResponse.id_token, tokenResponse.access_token)).pipe(tap((result) => this.storeIdToken(result)), map((_) => tokenResponse));
                }
                else {
                    return of(tokenResponse);
                }
            }))
                .subscribe((tokenResponse) => {
                this.storeAccessTokenResponse(tokenResponse.access_token, tokenResponse.refresh_token, tokenResponse.expires_in || this.config.fallbackAccessTokenExpirationTimeInSec, tokenResponse.scope);
                this.eventsSubject.next(new AuthSuccessEvent('token_received'));
                this.eventsSubject.next(new AuthSuccessEvent('token_refreshed'));
                resolve(tokenResponse);
            }, (err) => {
                this.eventsSubject.next(new AuthErrorEvent('token_refresh_error', err));
                reject(err);
            });
        });
    }
    /**
     * Revoke token and logout the user
     * Specific to Salesforce
     * - Revoke with HTTP request
     * - Logout through iframe
     * - Redirect to post logout url
     */
    revokeTokenAndLogout() {
        return new Promise((resolve, reject) => {
            let params = new HttpParams({ encoder: new WebHttpUrlEncodingCodec() });
            if (this.config.refreshTokenScope) {
                params = params.set('scope', this.config.scope);
            }
            const headers = new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded');
            params = params.set('client_id', this.config?.clientId);
            if (this.config.clientSecret) {
                params = params.set('client_secret', this.config.clientSecret);
            }
            if (this.hasValidAccessToken()) {
                params = params.set('token', this.getAccessToken()).set('token_type_hint', 'access_token');
            }
            let logoutUrl = this.config.logoutUrl;
            if (this.hasValidIdToken()) {
                logoutUrl = `${logoutUrl}${logoutUrl.indexOf('?') > -1 ? '&' : '?'}id_token_hint=${this.getIdToken()}`;
            }
            this.http.post(this.config.revocationEndpoint, params, { headers }).subscribe((res) => {
                this.clearStorage();
                this.logOutUsingIframe(logoutUrl);
                setTimeout(() => {
                    this.config.openUri(this.config.redirectUri);
                }, 1500);
                resolve(res);
            }, (err) => {
                this.eventsSubject.next(new AuthErrorEvent('token_revoke_error', err));
                reject(err);
            });
        });
    }
    /**
     * Logout using iframe
     * Use for Salesforce
     *
     * @param url
     */
    logOutUsingIframe(url) {
        const existingIframe = this.document.getElementById(this.config.logoutIFrameName);
        if (existingIframe) {
            this.document.body.removeChild(existingIframe);
        }
        const iframe = this.document.createElement('iframe');
        iframe.id = this.config.logoutIFrameName;
        iframe.setAttribute('src', url);
        iframe.style.display = 'none';
        this.document.body.appendChild(iframe);
    }
    validateUrlFromDiscoveryDocument(url) {
        const errors = [];
        const issuerCheck = this.validateUrlAgainstIssuer(url);
        if (!issuerCheck && !this.config.skipIssuerCheck) {
            errors.push('Every url in discovery document has to start with the issuer url.' + 'Also see property strictDiscoveryDocumentValidation.');
        }
        return errors;
    }
    validateUrlAgainstIssuer(url) {
        if (!url) {
            return true;
        }
        return url.toLowerCase().startsWith(this.config.issuer.toLowerCase());
    }
    setupRefreshTimer() {
        if (this.hasValidIdToken() || this.hasValidAccessToken()) {
            this.clearAccessTokenTimer();
            this.clearIdTokenTimer();
            this.setupExpirationTimers();
        }
        if (this.tokenReceivedSubscription) {
            this.tokenReceivedSubscription.unsubscribe();
        }
        this.tokenReceivedSubscription = this.events.pipe(filter((e) => e.type === 'token_received')).subscribe((_) => {
            this.clearAccessTokenTimer();
            this.clearIdTokenTimer();
            this.setupExpirationTimers();
        });
    }
    setupExpirationTimers() {
        if (this.hasValidAccessToken()) {
            this.setupAccessTokenTimer();
        }
        if (this.hasValidIdToken()) {
            this.setupIdTokenTimer();
        }
    }
    setupAccessTokenTimer() {
        const expiration = this.getAccessTokenExpiration();
        const storedAt = parseInt(this.storage.getItem('access_token_stored_at'), 10);
        const timeout = this.calcTimeout(storedAt, expiration);
        this.ngZone.runOutsideAngular(() => {
            this.accessTokenTimeoutSubscription = of(new AuthInfoEvent('token_expires', 'access_token'))
                .pipe(delay(timeout))
                .subscribe((e) => {
                this.ngZone.run(() => {
                    this.eventsSubject.next(e);
                });
            });
        });
    }
    setupIdTokenTimer() {
        const expiration = this.getIdTokenExpiration();
        const storedAt = parseInt(this.storage.getItem('id_token_stored_at'), 10);
        const timeout = this.calcTimeout(storedAt, expiration);
        this.ngZone.runOutsideAngular(() => {
            this.idTokenTimeoutSubscription = of(new AuthInfoEvent('token_expires', 'id_token'))
                .pipe(delay(timeout))
                .subscribe((e) => {
                this.ngZone.run(() => {
                    this.eventsSubject.next(e);
                });
            });
        });
    }
    clearAccessTokenTimer() {
        if (this.accessTokenTimeoutSubscription) {
            this.accessTokenTimeoutSubscription.unsubscribe();
        }
    }
    clearIdTokenTimer() {
        if (this.idTokenTimeoutSubscription) {
            this.idTokenTimeoutSubscription.unsubscribe();
        }
    }
    clearAutomaticRefreshTimer() {
        if (this.automaticRefreshSubscription) {
            this.automaticRefreshSubscription.unsubscribe();
        }
    }
    calcTimeout(storedAt, expiration) {
        const now = this.now();
        const delta = (expiration - storedAt) * this.config.timeoutFactor - (now - storedAt);
        return Math.max(0, delta);
    }
    /**
     * Loads the discovery document to configure most
     * properties of this service. The url of the discovery
     * document is infered from the issuer's url according
     * to the OpenId Connect spec. To use another url you
     * can pass it to to optional parameter fullUrl.
     *
     * @param fullUrl
     */
    loadDiscoveryDocument(fullUrl = null) {
        return this.initStorage().finally(() => {
            return new Promise((resolve, reject) => {
                if (this.discovered) {
                    resolve(null);
                    return;
                }
                if (!fullUrl) {
                    fullUrl = this.config.issuer || '';
                    if (!fullUrl.endsWith('/')) {
                        fullUrl += '/';
                    }
                    fullUrl += '.well-known/openid-configuration';
                }
                this.http.get(fullUrl).subscribe((doc) => {
                    if (!this.validateDiscoveryDocument(doc)) {
                        this.eventsSubject.next(new AuthErrorEvent('discovery_document_validation_error', null));
                        reject('discovery_document_validation_error');
                        return;
                    }
                    this.discovered = true;
                    this.config.loginUrl = doc.authorization_endpoint;
                    this.config.logoutUrl = this.config.logoutUrl || doc.end_session_endpoint;
                    this.grantTypesSupported = doc.grant_types_supported;
                    this.config.issuer = doc.issuer;
                    this.config.tokenEndpoint = this.config.tokenEndpoint || doc.token_endpoint;
                    this.config.userinfoEndpoint = this.config.userinfoEndpoint || doc.userinfo_endpoint;
                    this.jwksUri = doc.jwks_uri;
                    this.config.sessionCheckIFrameUrl = this.config.sessionCheckIFrameUrl || doc.check_session_iframe;
                    this.config.revocationEndpoint = this.config.revocationEndpoint || doc.revocation_endpoint;
                    if (this.config.sessionChecksEnabled) {
                        this.restartSessionChecksIfStillLoggedIn();
                    }
                    this.loadJwks()
                        .then((jwks) => {
                        const result = {
                            discoveryDocument: doc,
                            jwks
                        };
                        const event = new AuthSuccessEvent('discovery_document_loaded', result);
                        this.eventsSubject.next(event);
                        resolve(event);
                    })
                        .catch((err) => {
                        this.eventsSubject.next(new AuthErrorEvent('discovery_document_load_error', err));
                        reject(err);
                    });
                }, (err) => {
                    console.error('error loading discovery document', err);
                    this.eventsSubject.next(new AuthErrorEvent('discovery_document_load_error', err));
                    reject(err);
                });
            });
        });
    }
    initStorage() {
        if (!this.storage.init) {
            return Promise.resolve();
        }
        return this.storage.init();
    }
    loadJwks() {
        return new Promise((resolve, reject) => {
            if (this.jwksUri) {
                this.http.get(this.jwksUri).subscribe((jwks) => {
                    this.config.jwks = jwks;
                    this.eventsSubject.next(new AuthSuccessEvent('discovery_document_loaded'));
                    resolve(jwks);
                }, (err) => {
                    console.error('error loading jwks', err);
                    this.eventsSubject.next(new AuthErrorEvent('jwks_load_error', err));
                    reject(err);
                });
            }
            else {
                resolve(null);
            }
        });
    }
    validateDiscoveryDocument(doc) {
        let errors;
        if (!this.config.skipIssuerCheck && doc.issuer !== this.config.issuer) {
            console.error('invalid issuer in discovery document', 'expected: ' + this.config.issuer, 'current: ' + doc.issuer);
            return false;
        }
        errors = this.validateUrlFromDiscoveryDocument(doc.authorization_endpoint);
        if (errors.length > 0) {
            console.error('error validating authorization_endpoint in discovery document', errors);
            return false;
        }
        errors = this.validateUrlFromDiscoveryDocument(doc.end_session_endpoint);
        if (errors.length > 0) {
            console.error('error validating end_session_endpoint in discovery document', errors);
            return false;
        }
        errors = this.validateUrlFromDiscoveryDocument(doc.token_endpoint);
        if (errors.length > 0) {
            console.error('error validating token_endpoint in discovery document', errors);
        }
        errors = this.validateUrlFromDiscoveryDocument(doc.revocation_endpoint);
        if (errors.length > 0) {
            console.error('error validating revocation_endpoint in discovery document', errors);
        }
        errors = this.validateUrlFromDiscoveryDocument(doc.userinfo_endpoint);
        if (errors.length > 0) {
            console.error('error validating userinfo_endpoint in discovery document', errors);
            return false;
        }
        errors = this.validateUrlFromDiscoveryDocument(doc.jwks_uri);
        if (errors.length > 0) {
            console.error('error validating jwks_uri in discovery document', errors);
            return false;
        }
        if (this.config.sessionChecksEnabled && !doc.check_session_iframe) {
            console.warn('sessionChecksEnabled is activated but discovery document' + ' does not contain a check_session_iframe field');
        }
        return true;
    }
    /**
     * Loads the user profile by accessing the user info endpoint defined by OpenId Connect.
     *
     * When using this with Auth2 password flow, make sure that the property oidc is set to false.
     * Otherwise stricter validations take place that make this operation fail.
     */
    loadUserProfile() {
        if (!this.hasValidAccessToken()) {
            throw new Error('Can not load User Profile without access_token');
        }
        return new Promise((resolve, reject) => {
            const headers = new HttpHeaders().set('Authorization', 'Bearer ' + this.getAccessToken());
            this.http.get(this.config.userinfoEndpoint, { headers }).subscribe((userinfo) => {
                let info = userinfo;
                const existingClaims = this.getIdentityClaims();
                if (!this.config.skipSubjectCheck && (!existingClaims.sub || info.sub !== existingClaims.sub)) {
                    const err = 'if property oidc is true, the received user-id (sub) has to be the user-id ' +
                        'of the user that has logged in with oidc.\n' +
                        'if you are not using oidc but just Auth2 password flow set oidc to false';
                    reject(err);
                    return;
                }
                info = Object.assign({}, existingClaims, info);
                this.storage.setItem('id_token_claims_obj', JSON.stringify(info));
                this.eventsSubject.next(new AuthSuccessEvent('user_profile_loaded'));
                resolve({ info });
            }, (err) => {
                console.error('error loading user info', err);
                this.eventsSubject.next(new AuthErrorEvent('user_profile_load_error', err));
                reject(err);
            });
        });
    }
    /**
     * Check if session check could be enable
     * Watching configuration
     * Watching stored session state
     *
     * @returns
     */
    canPerformSessionCheck() {
        if (!this.config.sessionChecksEnabled) {
            return false;
        }
        if (!this.config.sessionCheckIFrameUrl) {
            console.warn('sessionChecksEnabled is activated but there is no sessionCheckIFrameUrl');
            return false;
        }
        const sessionState = this.getSessionState();
        if (!sessionState) {
            console.warn('sessionChecksEnabled is activated but there is no session_state');
            return false;
        }
        if (typeof this.document === 'undefined') {
            return false;
        }
        return true;
    }
    setupSessionCheckEventListener() {
        this.removeSessionCheckEventListener();
        this.sessionCheckEventListener = (e) => {
            const origin = e.origin.toLowerCase();
            const issuer = this.config.issuer.toLowerCase();
            if (!issuer.startsWith(origin)) {
                return;
            }
            // only run in Angular zone if it is 'changed' or 'error'
            switch (e.data) {
                case 'unchanged':
                    this.ngZone.run(() => {
                        this.handleSessionUnchanged();
                    });
                    break;
                case 'changed':
                    this.ngZone.run(() => {
                        this.handleSessionChange();
                    });
                    break;
                case 'error':
                    this.ngZone.run(() => {
                        this.handleSessionError();
                    });
                    break;
            }
        };
        // prevent Angular from refreshing the view on every message (runs in intervals)
        this.ngZone.runOutsideAngular(() => {
            window.addEventListener('message', this.sessionCheckEventListener);
        });
    }
    handleSessionUnchanged() {
        this.eventsSubject.next(new AuthInfoEvent('session_unchanged'));
    }
    handleSessionChange() {
        this.eventsSubject.next(new AuthInfoEvent('session_changed'));
        this.stopSessionCheckTimer();
        this.refreshToken().catch((_) => {
            this.eventsSubject.next(new AuthInfoEvent('session_terminated'));
            this.logOut(true);
        });
    }
    handleSessionError() {
        this.stopSessionCheckTimer();
        this.eventsSubject.next(new AuthInfoEvent('session_error'));
    }
    removeSessionCheckEventListener() {
        if (this.sessionCheckEventListener) {
            window.removeEventListener('message', this.sessionCheckEventListener);
            this.sessionCheckEventListener = null;
        }
    }
    /**
     * Init session check using iframe
     *
     * @returns
     */
    initSessionCheck() {
        if (!this.canPerformSessionCheck()) {
            return;
        }
        const existingIframe = this.document.getElementById(this.config.sessionCheckIFrameName);
        if (existingIframe) {
            this.document.body.removeChild(existingIframe);
        }
        const iframe = this.document.createElement('iframe');
        iframe.id = this.config.sessionCheckIFrameName;
        this.setupSessionCheckEventListener();
        const url = this.config.sessionCheckIFrameUrl;
        iframe.setAttribute('src', url);
        iframe.style.display = 'none';
        this.document.body.appendChild(iframe);
        this.startSessionCheckTimer();
    }
    startSessionCheckTimer() {
        this.stopSessionCheckTimer();
        this.ngZone.runOutsideAngular(() => {
            this.sessionCheckTimer = setInterval(this.checkSession.bind(this), this.config.sessionCheckIntervall);
        });
    }
    stopSessionCheckTimer() {
        if (this.sessionCheckTimer) {
            clearInterval(this.sessionCheckTimer);
            this.sessionCheckTimer = null;
        }
    }
    checkSession() {
        const iframe = this.document.getElementById(this.config.sessionCheckIFrameName);
        if (!iframe) {
            console.warn('checkSession did not find iframe', this.config.sessionCheckIFrameName);
        }
        const sessionState = this.getSessionState();
        if (!sessionState) {
            this.stopSessionCheckTimer();
        }
        const message = `${this.config.clientId} ${sessionState}`;
        iframe.contentWindow.postMessage(message, this.config.issuer);
    }
    /**
     * Create the login url
     */
    createLoginUrl(state = '', loginHint = '', customRedirectUri = '', noPrompt = false) {
        const redirectUri = customRedirectUri ? customRedirectUri : this.config.redirectUri;
        const nonce = this.createAndSaveNonce();
        if (state) {
            state = nonce + this.config.nonceStateSeparator + encodeURIComponent(state);
        }
        else {
            state = nonce;
        }
        const seperationChar = this.config.loginUrl.indexOf('?') > -1 ? '&' : '?';
        let scope = this.config.scope;
        if (!scope.match(/(^|\s)openid($|\s)/)) {
            scope = 'openid ' + scope;
        }
        let url = this.config.loginUrl +
            seperationChar +
            'response_type=code' +
            '&client_id=' +
            encodeURIComponent(this.config.clientId) +
            '&state=' +
            encodeURIComponent(state) +
            '&redirect_uri=' +
            encodeURIComponent(redirectUri) +
            '&scope=' +
            encodeURIComponent(scope);
        if (!this.config.disablePKCE) {
            const [challenge, verifier] = this.createChallangeVerifierPairForPKCE();
            this.storage.setItem('PKCE_verifier', verifier);
            url += '&code_challenge=' + challenge;
            url += '&code_challenge_method=S256';
        }
        if (loginHint) {
            url += '&login_hint=' + encodeURIComponent(loginHint);
        }
        if (this.config.resource) {
            url += '&resource=' + encodeURIComponent(this.config.resource);
        }
        if (!this.config.disableNonceCheck) {
            url += '&nonce=' + encodeURIComponent(nonce);
        }
        if (noPrompt) {
            url += '&prompt=none';
        }
        return url;
    }
    /**
     * Store access token in session or local storage
     *
     * @param accessToken
     * @param refreshToken
     * @param expiresIn
     * @param grantedScopes
     */
    storeAccessTokenResponse(accessToken, refreshToken, expiresIn, grantedScopes) {
        this.storage.setItem('access_token', accessToken);
        if (grantedScopes && !Array.isArray(grantedScopes)) {
            this.storage.setItem('granted_scopes', JSON.stringify(grantedScopes.split(' ')));
        }
        else if (grantedScopes && Array.isArray(grantedScopes)) {
            this.storage.setItem('granted_scopes', JSON.stringify(grantedScopes));
        }
        this.storage.setItem('access_token_stored_at', '' + this.now());
        if (expiresIn) {
            const expiresInMilliSeconds = expiresIn * 1000;
            const now = this.newDate();
            const expiresAt = now.getTime() + expiresInMilliSeconds;
            this.storage.setItem('expires_at', '' + expiresAt);
        }
        if (refreshToken) {
            this.storage.setItem('refresh_token', refreshToken);
        }
    }
    /**
     * Init login code flow
     *
     * Check nonce
     * Get token using authentication code and store retrieve data
     * Get user info if necessary
     *
     * @param options
     * @param route
     * @returns
     */
    async tryLoginCodeFlow(options = null, route) {
        options = options || {};
        const querySource = options.customHashFragment ? options.customHashFragment.substring(1) : window.location.search;
        const parts = this.urlHelper.getCodePartsFromUrl(querySource);
        const code = parts['code'];
        const state = parts['state'];
        const sessionState = parts['session_state'];
        const href = location.origin +
            location.pathname +
            location.search
                .replace(/code=[^&\$]*/, '')
                .replace(/scope=[^&\$]*/, '')
                .replace(/state=[^&\$]*/, '')
                .replace(/session_state=[^&\$]*/, '')
                .replace(/iss=[^&\$]*/, '')
                .replace(/client_id=[^&\$]*/, '')
                .replace(/sfdc_community_url=[^&\$]*/, '')
                .replace(/sfdc_community_id=[^&\$]*/, '')
                .replace(/^\?&/, '?')
                .replace(/&$/, '')
                .replace(/^\?$/, '')
                .replace(/&+/g, '&')
                .replace(/\?&/, '?')
                .replace(/\?$/, '') +
            location.hash;
        history.replaceState(null, window.name, href);
        const [nonceInState, userState] = this.parseState(state);
        this.state = userState;
        if (parts['error']) {
            this.clearLocationHash();
            const err = new AuthErrorEvent('code_error', {}, parts);
            this.eventsSubject.next(err);
            return Promise.reject(err);
        }
        if (!nonceInState) {
            this.storage.setItem('requested_route', route || window.location.pathname + window.location.search);
            return Promise.resolve();
        }
        const success = this.validateNonce(nonceInState);
        if (!success) {
            const event = new AuthErrorEvent('invalid_nonce_in_state', null);
            this.eventsSubject.next(event);
            return Promise.reject(event);
        }
        this.storeSessionState(sessionState);
        if (code) {
            await this.getTokenFromCode(code, options);
            if (this.config.triggerUserInfoAfterLogin && this.getIdToken()) {
                await this.loadUserProfile();
            }
            return Promise.resolve();
        }
        return Promise.reject();
    }
    /**
     * Get token using an intermediate code. Works for the Authorization Code flow.
     */
    getTokenFromCode(code, options) {
        let params = new HttpParams({ encoder: new WebHttpUrlEncodingCodec() })
            .set('grant_type', 'authorization_code')
            .set('code', code)
            .set('redirect_uri', options.customRedirectUri || this.config.redirectUri);
        if (!this.config.disablePKCE) {
            const PKCEVerifier = this.storage.getItem('PKCE_verifier');
            if (!PKCEVerifier) {
                console.warn('No PKCE verifier found in Auth storage!');
            }
            else {
                params = params.set('code_verifier', PKCEVerifier);
            }
        }
        return this.fetchAndProcessToken(params, options);
    }
    /**
     * Do http request to retrieve tokens using client id / secret and authentication code
     *
     * @param params
     * @param options
     * @returns
     */
    fetchAndProcessToken(params, options) {
        options = options || {};
        const headers = new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded');
        params = params.set('client_id', this.config.clientId);
        if (this.config.clientSecret) {
            params = params.set('client_secret', this.config.clientSecret);
        }
        return new Promise((resolve, reject) => {
            this.http.post(this.config.tokenEndpoint, params, { headers }).subscribe((tokenResponse) => {
                this.storeAccessTokenResponse(tokenResponse.access_token, tokenResponse.refresh_token, tokenResponse.expires_in || this.config.fallbackAccessTokenExpirationTimeInSec, tokenResponse.scope);
                if (tokenResponse.id_token) {
                    this.processIdToken(tokenResponse.id_token, tokenResponse.access_token)
                        .then((result) => {
                        this.storeIdToken(result);
                        this.eventsSubject.next(new AuthSuccessEvent('token_received'));
                        this.eventsSubject.next(new AuthSuccessEvent('token_refreshed'));
                        resolve(tokenResponse);
                    })
                        .catch((reason) => {
                        this.eventsSubject.next(new AuthErrorEvent('token_validation_error', reason));
                        console.error('Error validating tokens', reason);
                        reject(reason);
                    });
                }
                else {
                    this.eventsSubject.next(new AuthSuccessEvent('token_received'));
                    this.eventsSubject.next(new AuthSuccessEvent('token_refreshed'));
                    resolve(tokenResponse);
                }
            }, (err) => {
                console.error('Error getting token', err);
                this.eventsSubject.next(new AuthErrorEvent('token_error', err));
                reject(err);
            });
        });
    }
    parseState(state) {
        let nonce = state;
        let userState = '';
        if (state) {
            const idx = state.indexOf(this.config.nonceStateSeparator);
            if (idx > -1) {
                nonce = state.substring(0, idx);
                userState = state.substring(idx + this.config.nonceStateSeparator.length);
            }
        }
        return [nonce, userState];
    }
    validateNonce(nonceInState) {
        const savedNonce = this.storage.getItem('nonce');
        if (savedNonce !== nonceInState && !this.config.disableNonceCheck) {
            const err = 'Validating access_token failed, wrong state/nonce.';
            console.error(err, savedNonce, nonceInState);
            return false;
        }
        return true;
    }
    storeIdToken(idToken) {
        this.storage.setItem('id_token', idToken.idToken);
        const existingClaims = this.storage.getItem('id_token_claims_obj');
        let claims = idToken.idTokenClaimsJson;
        if (existingClaims) {
            claims = JSON.stringify({ ...JSON.parse(existingClaims), ...idToken.idTokenClaims });
        }
        this.storage.setItem('id_token_claims_obj', claims);
        this.storage.setItem('id_token_expires_at', '' + idToken.idTokenExpiresAt);
        this.storage.setItem('id_token_stored_at', '' + this.now());
    }
    storeSessionState(sessionState) {
        this.storage.setItem('session_state', sessionState);
    }
    getSessionState() {
        return this.storage.getItem('session_state');
    }
    processIdToken(idToken, accessToken) {
        const tokenParts = idToken.split('.');
        const headerBase64 = padBase64(tokenParts[0]);
        const headerJson = b64DecodeUnicode(headerBase64);
        const header = JSON.parse(headerJson);
        const claimsBase64 = padBase64(tokenParts[1]);
        const claimsJson = b64DecodeUnicode(claimsBase64);
        const claims = JSON.parse(claimsJson);
        const savedNonce = this.storage.getItem('nonce');
        if (Array.isArray(claims.aud)) {
            if (claims.aud.every((v) => v !== this.config.clientId)) {
                const err = 'Wrong audience: ' + claims.aud.join(',');
                console.warn(err);
                return Promise.reject(err);
            }
        }
        else {
            if (claims.aud !== this.config.clientId) {
                const err = 'Wrong audience: ' + claims.aud;
                console.warn(err);
                return Promise.reject(err);
            }
        }
        if (!claims.sub) {
            const err = 'No sub claim in id_token';
            console.warn(err);
            return Promise.reject(err);
        }
        /* For now, we only check whether the sub against
         * silentRefreshSubject when sessionChecksEnabled is on
         * We will reconsider in a later version to do this
         * in every other case too.
         */
        if (this.config.sessionChecksEnabled && this.silentRefreshSubject && this.silentRefreshSubject !== claims['sub']) {
            const err = 'After refreshing, we got an id_token for another user (sub). ' +
                `Expected sub: ${this.silentRefreshSubject}, received sub: ${claims['sub']}`;
            console.warn(err);
            return Promise.reject(err);
        }
        if (!claims.iat) {
            const err = 'No iat claim in id_token';
            console.warn(err);
            return Promise.reject(err);
        }
        if (!this.config.skipIssuerCheck && claims.iss !== this.config.issuer) {
            const err = 'Wrong issuer: ' + claims.iss;
            console.warn(err);
            return Promise.reject(err);
        }
        if (claims.nonce !== savedNonce && !this.config.disableNonceCheck) {
            const err = 'Wrong nonce: ' + claims.nonce;
            console.warn(err);
            return Promise.reject(err);
        }
        const now = this.now();
        const expiresAtMSec = claims.exp * 1000;
        const clockSkewInMSec = this.clockSkewInSec * 1000;
        if (expiresAtMSec + clockSkewInMSec <= now) {
            const err = 'Token has expired';
            console.error(err, {
                now,
                expiresAtMSec
            });
            return Promise.reject(err);
        }
        const validationParams = {
            accessToken,
            idToken,
            jwks: this.config.jwks,
            idTokenClaims: claims,
            idTokenHeader: header,
            loadKeys: () => this.loadJwks()
        };
        return this.checkSignature(validationParams).then((_) => {
            return {
                idToken,
                idTokenClaims: claims,
                idTokenClaimsJson: claimsJson,
                idTokenHeader: header,
                idTokenHeaderJson: headerJson,
                idTokenExpiresAt: expiresAtMSec
            };
        });
    }
    /**
     * Returns the received claims about the user.
     */
    getIdentityClaims() {
        const claims = this.storage.getItem('id_token_claims_obj');
        return claims ? JSON.parse(claims) : null;
    }
    /**
     * Returns the granted scopes from the server.
     */
    getGrantedScopes() {
        const scopes = this.storage.getItem('granted_scopes');
        return scopes ? JSON.parse(scopes) : null;
    }
    /**
     * Returns the current id_token.
     */
    getIdToken() {
        return this.storage ? this.storage.getItem('id_token') : null;
    }
    /**
     * Returns the current access token.
     */
    getAccessToken() {
        return this.storage ? this.storage.getItem('access_token') : null;
    }
    /**
     * Returns the current refresh token.
     */
    getRefreshToken() {
        return this.storage ? this.storage.getItem('refresh_token') : null;
    }
    /**
     * Returns the expiration date of the access_token
     * as milliseconds since 1970.
     */
    getAccessTokenExpiration() {
        if (!this.storage.getItem('expires_at')) {
            return null;
        }
        return parseInt(this.storage.getItem('expires_at'), 10);
    }
    /**
     * Returns the expiration date of the id_token
     * as milliseconds since 1970.
     */
    getIdTokenExpiration() {
        if (!this.storage.getItem('id_token_expires_at')) {
            return null;
        }
        return parseInt(this.storage.getItem('id_token_expires_at'), 10);
    }
    /**
     * Checkes, whether there is a valid access_token.
     */
    hasValidAccessToken() {
        if (this.getAccessToken()) {
            const expiresAt = this.storage.getItem('expires_at');
            if (expiresAt && parseInt(expiresAt, 10) < this.newDate().getTime() - this.clockSkewInSec) {
                return false;
            }
            return true;
        }
        return false;
    }
    /**
     * Checks whether there is a valid id_token.
     */
    hasValidIdToken() {
        if (this.getIdToken()) {
            const expiresAt = this.storage.getItem('id_token_expires_at');
            if (expiresAt && parseInt(expiresAt, 10) < this.newDate().getTime() - this.clockSkewInSec) {
                return false;
            }
            return true;
        }
        return false;
    }
    /**
     * Removes all tokens and logs the user out.
     * If a logout url is configured, the user is
     * redirected to it with optional state parameter.
     */
    logOut(noRedirectToLogoutUrl) {
        const id_token = this.getIdToken();
        this.silentRefreshSubject = null;
        this.eventsSubject.next(new AuthInfoEvent('logout'));
        if (noRedirectToLogoutUrl) {
            this.clearStorage();
            return;
        }
        if (!this.config.logoutUrl || !id_token) {
            return;
        }
        let logoutUrl;
        const postLogoutUrl = this.config.postLogoutRedirectUri || this.config.redirectUri || '';
        // For backward compatibility
        if (this.config.logoutUrl.indexOf('{{') > -1) {
            logoutUrl = this.config.logoutUrl
                .replace(/\{\{id_token\}\}/, encodeURIComponent(id_token))
                .replace(/\{\{client_id\}\}/, encodeURIComponent(this.config.clientId));
        }
        else {
            logoutUrl = this.config.logoutUrl;
            if (postLogoutUrl) {
                logoutUrl += `${logoutUrl.indexOf('?') > -1 ? '&' : '?'}post_logout_redirect_uri=${postLogoutUrl}`;
            }
            if (id_token && this.hasValidIdToken()) {
                logoutUrl = `${logoutUrl}${logoutUrl.indexOf('?') > -1 ? '&' : '?'}id_token_hint=${this.getIdToken()}`;
            }
        }
        this.clearStorage();
        this.config.openUri(logoutUrl);
    }
    /**
     * Clear all authentication keys stored in the choose storage
     */
    clearStorage() {
        this.storage.removeItem('access_token');
        this.storage.removeItem('id_token');
        this.storage.removeItem('refresh_token');
        this.storage.removeItem('nonce');
        this.storage.removeItem('PKCE_verifier');
        this.storage.removeItem('expires_at');
        this.storage.removeItem('id_token_claims_obj');
        this.storage.removeItem('id_token_expires_at');
        this.storage.removeItem('id_token_stored_at');
        this.storage.removeItem('access_token_stored_at');
        this.storage.removeItem('granted_scopes');
        this.storage.removeItem('session_state');
    }
    createAndSaveNonce() {
        const nonce = this.createNonce();
        this.storage.setItem('nonce', nonce);
        return nonce;
    }
    ngOnDestroy() {
        this.clearAccessTokenTimer();
        this.clearIdTokenTimer();
        this.stopSessionCheckTimer();
        this.removeSessionCheckEventListener();
        const sessionCheckFrame = this.document.getElementById(this.config.sessionCheckIFrameName);
        if (sessionCheckFrame) {
            sessionCheckFrame.remove();
        }
        if (this.userActivityTimer) {
            clearTimeout(this.userActivityTimer);
        }
    }
    createNonce() {
        /*
         * This alphabet is from:
         * https://tools.ietf.org/html/rfc7636#section-4.1
         *
         * [A-Z] / [a-z] / [0-9] / "-" / "." / "_" / "~"
         */
        const unreserved = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
        let size = 45;
        let id = '';
        const crypto = typeof self === 'undefined' ? null : self.crypto || self['msCrypto'];
        if (crypto) {
            let bytes = new Uint8Array(size);
            crypto.getRandomValues(bytes);
            // Needed for IE
            if (!bytes.map) {
                bytes.map = Array.prototype.map;
            }
            bytes = bytes.map((x) => unreserved.charCodeAt(x % unreserved.length));
            id = String.fromCharCode.apply(null, Array.from(bytes));
        }
        else {
            while (0 < size--) {
                id += unreserved[(Math.random() * unreserved.length) | 0];
            }
        }
        return base64UrlEncode(id);
    }
    async checkAtHash(params) {
        if (!this.tokenValidationHandler) {
            console.warn('No tokenValidationHandler configured. Cannot check at_hash.');
            return true;
        }
        return this.tokenValidationHandler.validateAtHash(params);
    }
    checkSignature(params) {
        if (!this.tokenValidationHandler) {
            console.warn('No tokenValidationHandler configured. Cannot check signature.');
            return Promise.resolve(null);
        }
        return this.tokenValidationHandler.validateSignature(params);
    }
    /**
     * Starts the authorization code flow and redirects to user to
     * the auth servers login url.
     */
    initCodeFlow(additionalState = '') {
        if (this.config.loginUrl !== '') {
            this.initCodeFlowInternal(additionalState);
        }
        else {
            this.events
                .pipe(filter((e) => e.type === 'discovery_document_loaded'))
                .subscribe((_) => this.initCodeFlowInternal(additionalState));
        }
    }
    initCodeFlowInternal(additionalState = '') {
        this.config.openUri(this.createLoginUrl(additionalState, null, null, false));
    }
    createChallangeVerifierPairForPKCE() {
        if (!this.crypto) {
            throw new Error('PKCE support for code flow needs a CryptoHander. Did you import the AuthModule using forRoot() ?');
        }
        const verifier = this.createNonce();
        const challengeRaw = this.crypto.calcHash(verifier);
        const challenge = base64UrlEncode(challengeRaw);
        return [challenge, verifier];
    }
    /**
     * Clear location.hash if it's present
     */
    clearLocationHash() {
        // Checking for empty hash is necessary for Firefox
        // as setting an empty hash to an empty string adds # to the URL
        if (location.hash !== '') {
            location.hash = '';
        }
    }
    /**
     * Get the current date
     */
    now() {
        return Date.now();
    }
    /**
     * Generate a new date
     */
    newDate() {
        return new Date();
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: AuthService, deps: [{ token: i0.NgZone }, { token: i1.HttpClient }, { token: i2.Router }, { token: UrlService }, { token: i4.PlatformLocation }, { token: AuthStorage, optional: true }, { token: ValidationHandler, optional: true }, { token: HashHandler, optional: true }, { token: DOCUMENT }, { token: AUTH_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: AuthService });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: AuthService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: i0.NgZone }, { type: i1.HttpClient }, { type: i2.Router }, { type: UrlService }, { type: i4.PlatformLocation }, { type: AuthStorage, decorators: [{
                    type: Optional
                }] }, { type: ValidationHandler, decorators: [{
                    type: Optional
                }] }, { type: HashHandler, decorators: [{
                    type: Optional
                }] }, { type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: AuthConfig, decorators: [{
                    type: Inject,
                    args: [AUTH_CONFIG]
                }] }] });

/**
 * Manage authentication bearer to make api call
 *
 * Automatically, request are cloned to inject Authorization header with valid oidc token.
 * It's possible to restrict this method for a set of url. In this case, use bearerAllowedUrls in the module configuration.
 */
class DefaultAuthInterceptor {
    authService;
    constructor(authService) {
        this.authService = authService;
    }
    /**
     * Check if the Url need a authorization header
     *
     * @param url
     * @returns
     */
    checkUrl(url) {
        if (this.authService.config.bearerAllowedUrls) {
            return !!this.authService.config.bearerAllowedUrls.find((u) => url.toLowerCase().startsWith(u.toLowerCase()));
        }
        // Check if url is not the well known
        if (url.includes('.well-known/openid-configuration') || url.includes('connect/jwk_uri')) {
            return false;
        }
        return true;
    }
    /**
     * Clone the request and inject bearer
     *
     * @param req
     * @returns
     */
    getAuthRequest(req) {
        const url = req.url.toLowerCase();
        if (!this.authService.getAccessToken() || !this.checkUrl(url) || url === this.authService.config.tokenEndpoint) {
            return req;
        }
        return req.clone({
            setHeaders: {
                Authorization: `Bearer ${this.authService.getAccessToken()}`
            }
        });
    }
    intercept(req, next) {
        return next.handle(this.getAuthRequest(req));
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: DefaultAuthInterceptor, deps: [{ token: AuthService }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: DefaultAuthInterceptor });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: DefaultAuthInterceptor, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: AuthService }] });

/**
 * Method use with angular app_initializer
 *
 * Allow to require login when angular app is initialized
 * In this case AuthGuard are not required
 *
 * @param authService
 * @param router
 * @returns
 */
function authInitializer(authService) {
    return () => {
        authService.configure();
        authService.setupAutomaticSilentRefresh('access_token');
        if (!authService.config.authOnAppInitializer) {
            return Promise.resolve();
        }
        return authService.loadDiscoveryDocument().then(() => {
            return authService.tryLoginCodeFlow().then(() => {
                // No access token
                if (!authService.getAccessToken()) {
                    // Init code flow to redirect user on the login page
                    authService.initCodeFlow();
                    // Delay to not validate app initializer during redirect
                    return new Promise((resolve) => setTimeout(resolve, 3000));
                }
                else if (authService.getAccessToken() && !authService.hasValidAccessToken()) {
                    // Acces token exists but was expired
                    return authService.refreshToken().then(() => {
                        return Promise.resolve();
                    }, () => {
                        // Init code flow to redirect user on the login page
                        authService.initCodeFlow();
                        // Delay to not validate app initializer during redirect
                        return new Promise((resolve) => setTimeout(resolve, 3000));
                    });
                }
                else {
                    // User is logged
                    authService.navigateToRequestedRoute();
                }
            }, () => {
                authService.navigateToErrorRoute();
            });
        });
    };
}
function createDefaultStorage() {
    return sessionStorage;
}

/**
 * Validates the signature of an id_token against one
 * of the keys of an JSON Web Key Set (jwks).
 *
 * This jwks can be provided by the discovery document.
 */
class JwksValidationHandler extends AbstractValidationHandler {
    /**
     * Allowed algorithms
     */
    allowedAlgorithms = ['HS256', 'HS384', 'HS512', 'RS256', 'RS384', 'RS512', 'ES256', 'ES384', 'PS256', 'PS384', 'PS512'];
    /**
     * Time period in seconds the timestamp in the signature can
     * differ from the current time.
     */
    gracePeriodInSec = 600;
    validateSignature(params, retry = false) {
        if (!params.idToken) {
            throw new Error('Parameter idToken expected!');
        }
        if (!params.idTokenHeader) {
            throw new Error('Parameter idTokenHandler expected.');
        }
        if (!params.jwks) {
            throw new Error('Parameter jwks expected!');
        }
        if (!params.jwks['keys'] || !Array.isArray(params.jwks['keys']) || params.jwks['keys'].length === 0) {
            throw new Error('Array keys in jwks missing!');
        }
        const kid = params.idTokenHeader['kid'];
        const keys = params.jwks['keys'];
        let key;
        const alg = params.idTokenHeader['alg'];
        if (kid) {
            key = keys.find((k) => k['kid'] === kid /* && k['use'] === 'sig' */);
        }
        else {
            const kty = this.alg2kty(alg);
            const matchingKeys = keys.filter((k) => k['kty'] === kty && k['use'] === 'sig');
            if (matchingKeys.length > 1) {
                let error = 'More than one matching key found. Please specify a kid in the id_token header.';
                console.error(error);
                return Promise.reject(error);
            }
            else if (matchingKeys.length === 1) {
                key = matchingKeys[0];
            }
        }
        if (!key && !retry && params.loadKeys) {
            return params
                .loadKeys()
                .then((loadedKeys) => (params.jwks = loadedKeys))
                .then((_) => this.validateSignature(params, true));
        }
        if (!key && retry && !kid) {
            const error = 'No matching key found.';
            console.error(error);
            return Promise.reject(error);
        }
        if (!key && retry && kid) {
            const error = 'expected key not found in property jwks. ' +
                'This property is most likely loaded with the ' +
                'discovery document. ' +
                'Expected key id (kid): ' +
                kid;
            console.error(error);
            return Promise.reject(error);
        }
        const keyObj = rs.KEYUTIL.getKey(key);
        const validationOptions = {
            alg: this.allowedAlgorithms,
            gracePeriod: this.gracePeriodInSec
        };
        const isValid = rs.KJUR.jws.JWS.verifyJWT(params.idToken, keyObj, validationOptions);
        if (isValid) {
            return Promise.resolve();
        }
        else {
            return Promise.reject('Signature not valid');
        }
    }
    alg2kty(alg) {
        switch (alg.charAt(0)) {
            case 'R':
                return 'RSA';
            case 'E':
                return 'EC';
            default:
                throw new Error('Cannot infer kty from alg: ' + alg);
        }
    }
    calcHash(valueToHash, algorithm) {
        let hashAlg = new rs.KJUR.crypto.MessageDigest({ alg: algorithm });
        let result = hashAlg.digestString(valueToHash);
        let byteArrayAsString = this.toByteArrayAsString(result);
        return Promise.resolve(byteArrayAsString);
    }
    toByteArrayAsString(hexString) {
        let result = '';
        for (let i = 0; i < hexString.length; i += 2) {
            let hexDigit = hexString.charAt(i) + hexString.charAt(i + 1);
            let num = parseInt(hexDigit, 16);
            result += String.fromCharCode(num);
        }
        return result;
    }
}

/**
 * Angular guard to protect routes and trigger login flow
 * To use guard, module authOnAppInitializer option need to be false.
 *
 * Add this Guard on your module routing file to secure screens with CanActivate and CanActivateChild
 */
class AuthGuard {
    authService;
    platformLocation;
    constructor(authService, platformLocation) {
        this.authService = authService;
        this.platformLocation = platformLocation;
    }
    canActivate(route, state) {
        this.authService.configure();
        if (this.authService.config.authOnAppInitializer) {
            return true;
        }
        this.authService.setupAutomaticSilentRefresh('access_token');
        return this.authService.loadDiscoveryDocument().then(() => {
            if (this.authService.hasValidAccessToken()) {
                return true;
            }
            return this.authService.tryLoginCodeFlow(null, this.getResolvedUrl(state)).then(() => {
                // No access token
                if (!this.authService.getAccessToken()) {
                    // Init code flow to redirect user on the login page
                    this.authService.initCodeFlow();
                    return false;
                }
                else if (this.authService.getAccessToken() && !this.authService.hasValidAccessToken()) {
                    // Acces token exists but was expired
                    return this.authService.refreshToken().then(() => {
                        return Promise.resolve(true);
                    }, () => {
                        // Init code flow to redirect user on the login page
                        this.authService.initCodeFlow();
                        return false;
                    });
                }
                this.authService.navigateToRequestedRoute();
                return true;
            });
        });
    }
    canActivateChild(route, state) {
        return this.canActivate(route, state);
    }
    getResolvedUrl(state) {
        let url = state.url;
        const locale = this.platformLocation.getBaseHrefFromDOM();
        return locale && locale.length !== 0 ? `${locale.replace(/\/+$/, '')}${url}` : url;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: AuthGuard, deps: [{ token: AuthService }, { token: i4.PlatformLocation }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: AuthGuard });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: AuthGuard, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: AuthService }, { type: i4.PlatformLocation }] });

class IsAuthenticatedDirective {
    templateRef;
    viewContainer;
    authService;
    constructor(templateRef, viewContainer, authService) {
        this.templateRef = templateRef;
        this.viewContainer = viewContainer;
        this.authService = authService;
        const claims = this.authService.getIdentityClaims();
        // If claims are present, display template
        if (claims) {
            this.viewContainer.createEmbeddedView(this.templateRef);
        }
        else {
            this.viewContainer.clear();
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: IsAuthenticatedDirective, deps: [{ token: i0.TemplateRef }, { token: i0.ViewContainerRef }, { token: AuthService }], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.0.7", type: IsAuthenticatedDirective, selector: "[micIsAuthenticated]", ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: IsAuthenticatedDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[micIsAuthenticated]'
                }]
        }], ctorParameters: () => [{ type: i0.TemplateRef }, { type: i0.ViewContainerRef }, { type: AuthService }] });

class AuthModule {
    static forRoot(config = {}) {
        return {
            ngModule: AuthModule,
            providers: [
                AuthService,
                UrlService,
                AuthGuard,
                { provide: ValidationHandler, useClass: JwksValidationHandler },
                { provide: HashHandler, useClass: DefaultHashHandler },
                { provide: AUTH_CONFIG, useValue: new AuthConfig(config) },
                {
                    provide: APP_INITIALIZER,
                    useFactory: authInitializer,
                    deps: [AuthService],
                    multi: true
                },
                {
                    provide: HTTP_INTERCEPTORS,
                    useClass: DefaultAuthInterceptor,
                    multi: true
                }
            ]
        };
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: AuthModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.7", ngImport: i0, type: AuthModule, declarations: [IsAuthenticatedDirective], imports: [CommonModule], exports: [IsAuthenticatedDirective] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: AuthModule, imports: [CommonModule] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: AuthModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [IsAuthenticatedDirective],
                    exports: [IsAuthenticatedDirective]
                }]
        }] });

/*
 * Public API Surface of auth
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AUTH_CONFIG, AbstractValidationHandler, AuthConfig, AuthErrorEvent, AuthEvent, AuthGuard, AuthInfoEvent, AuthModule, AuthService, AuthStorage, AuthSuccessEvent, DefaultAuthInterceptor, IsAuthenticatedDirective, LoginOptions, ReceivedTokens, UrlService, ValidationHandler };
